import { models } from '@ynomia/core';

/** Drives the status icon and colour. */
export type StatusTone = 'progress' | 'queued' | 'success' | 'warning' | 'error';

export type StatusDescription = {
  label: string,
  tone: StatusTone,
  inProgress: boolean,
  failed: boolean,
};

const describe = (
  label: string,
  tone: StatusTone,
  { inProgress = false, failed = false } = {},
): StatusDescription => ({
  label, tone, inProgress, failed,
});

/**
 * How each server-side status reads to a user. Statuses are set by the backend only.
 *
 * A failed mapping is a warning rather than an error: a failed re-map leaves the previous
 * mapping in place and usable, and legacy models that were never mapped land here too.
 */
const STATUS_DESCRIPTIONS: Record<models.ModelStatus, StatusDescription> = {
  draft: describe('Upload incomplete', 'warning'),
  translating: describe('Translating', 'progress', { inProgress: true }),
  awaiting_mapping: describe('Queued for mapping', 'queued', { inProgress: true }),
  mapping: describe('Mapping', 'progress', { inProgress: true }),
  mapped: describe('Mapped', 'success'),
  translation_failed: describe('Translation failed', 'error', { failed: true }),
  mapping_failed: describe('Mapping failed', 'warning', { failed: true }),
};

export const describeStatus = (status?: models.ModelStatus): StatusDescription | undefined => (
  status ? STATUS_DESCRIPTIONS[status] : undefined
);

export const canRemap = (status?: models.ModelStatus): boolean => (
  status === 'mapped' || status === 'mapping_failed'
);

export const remapUnavailableReason = (status?: models.ModelStatus): string | undefined => {
  if (canRemap(status)) return undefined;
  if (status && describeStatus(status)?.inProgress) return 'Already being processed';
  return 'Available once the model has been translated';
};
